import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Loader, Tiles, ContainerControlSite, Content, Files, Text } from '@simplybenefits/sb-ui-library';
import Header from '../../components/Header.js';
import ActiveAccountList from './ActiveAccountList.js';
import PendingAccountList from './PendingAccountList.js';
import LostAccountList from './LostAccountList.js';
import Quotes from './Quotes.js';
import Settings from './Settings.js';
import Reports from './Reports.js';
import { getAdvisor } from '../../actions/advisors.js';
import permissionLevel from '../../services/permissions.js';
import PermissionsComponent from '../../components/PermissionsComponent.js';
import {
    listFiles,
    createFolder,
    uploadFile,
    downloadFile,
    removeFile,
    makePrivate,
    makePublic
} from '../../actions/files.js';

class AdvisorDashboard extends PureComponent {
    state = {
        selectedSection: null,
        goToStage: null
    };

    static getDerivedStateFromProps(props) {
        if (!permissionLevel('Advisors', props.accessType).includes('read')) {
            window.location.href = '/403';
        }

        return null;
    }

    componentDidMount() {
        this.props.dispatch(getAdvisor(this.props.match.params.advisorID));

        const url = window.location.href;
        const temp = url.split('?');
        const section = temp[1];

        if (section) {
            this.setState({
                selectedSection: section.split('=')[1]
            });
        }
    }

    _selectSection = (section) => {
        this.setState({
            selectedSection: section
        });
    };

    _buildBreadcrumbs = () => {
        if (!this.state.selectedSection) {
            return [
                {
                    title: 'Advisors',
                    link: `/advisors`
                }
            ];
        }

        return [
            {
                title: 'Advisors',
                link: `/advisors`
            },
            {
                title: `${ this.props.currentAdvisor.company } - ${ this.props.currentAdvisor.firstname } ${ this.props.currentAdvisor.lastname }`,
                onClick: () => this._selectSection(null)
            }
        ];
    };

    _getSubHeader = () => {
        if (this.state.selectedSection === 'advisor-active-accounts') {
            return 'Active Accounts';
        } else if (this.state.selectedSection === 'advisor-pending-accounts') {
            return 'Pending Accounts';
        } else if (this.state.selectedSection === 'advisor-lost-accounts') {
            return 'Lost Accounts';
        } else if (this.state.selectedSection === 'advisor-reports') {
            return 'Reports';
        } else if (this.state.selectedSection === 'advisor-settings') {
            return 'Advisor Settings';
        } else if (this.state.selectedSection === 'advisor-notifications') {
            return 'Notifications';
        } else if (this.state.selectedSection === 'advisor-quotes') {
            return 'Quotes';
        } else if (this.state.selectedSection === 'advisor-files') {
            return 'Files';
        }

        return `${ this.props.currentAdvisor.company } - ${ this.props.currentAdvisor.firstname } ${ this.props.currentAdvisor.lastname }`;

    };

    render() {
        const { currentAdvisor, accessType } = this.props;

        if (!this.props.currentAdvisor) {
            return <Loader />;
        }

        const subHeader = this._getSubHeader();
        const breadcrumbs = this._buildBreadcrumbs();

        const tiles = [
            {
                title: 'Quotes',
                description: 'Continue your setup progress on your prospect quotes.',
                onClick: () => this._selectSection('advisor-quotes'),
                testid: 'advisor-quotes-tile'
            },
            {
                title: 'Pending Accounts',
                description: 'Sold Accounts awaiting final changes.',
                onClick: () => this._selectSection('advisor-pending-accounts'),
                testid: 'advisor-pending-accounts-tile'
            },
            {
                title: 'Active Accounts',
                description: 'View and manage your currently active accounts.',
                onClick: () => this._selectSection('advisor-active-accounts'),
                testid: 'advisor-active-accounts-tile'
            },
            {
                title: 'Lost Opportunities',
                description: 'View data on your lost accounts.',
                onClick: () => this._selectSection('advisor-lost-accounts'),
                testid: 'advisor-lost-accounts-tile'
            }
        ];

        permissionLevel('Advisor Files', accessType).includes('read') &&
            tiles.push({
                title: 'Files',
                description: 'View and manager Advisor files.',
                onClick: () => this._selectSection('advisor-files'),
                testid: 'advisor-files-tile'
            });

        permissionLevel('Reports', accessType).includes('read') &&
            tiles.push({
                title: 'Reports',
                description: 'Generate and export reports of analytics.',
                onClick: () => this._selectSection('advisor-reports'),
                testid: 'advisor-reports-tile'
            });

        permissionLevel('Advisor Settings', accessType).includes('read') &&
            tiles.push({
                title: 'Settings',
                description: 'View and edit advisor settings.',
                onClick: () => this._selectSection('advisor-settings'),
                testid: 'advisor-settings-tile'
            });

        return (
            <ContainerControlSite background={"true"}>
                <Header
                    subHeader={subHeader}
                    breadcrumbs={breadcrumbs}
                    selectedTitle="advisors"
                />

                {
                    this.state.selectedSection === 'advisor-quotes' &&
                        <Quotes
                            meta={currentAdvisor}
                        />
                }

                {
                    this.state.selectedSection === 'advisor-active-accounts' &&
                        <ActiveAccountList
                            meta={currentAdvisor}
                        />
                }

                {
                    this.state.selectedSection === 'advisor-pending-accounts' &&
                        <PendingAccountList
                            meta={currentAdvisor}
                        />
                }

                {
                    this.state.selectedSection === 'advisor-lost-accounts' &&
                        <LostAccountList
                            meta={currentAdvisor}
                        />
                }

                <Content>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Text bold>
                            Brokerage: {currentAdvisor.brokerage}
                        </Text>
                    </div>
                    {
                        this.state.selectedSection === null &&
                            <Tiles tiles={tiles} />
                    }

                    {
                        this.state.selectedSection === 'advisor-settings' &&
                            <PermissionsComponent
                                permission="Advisor Settings"
                                accessType={this.props.accessType}
                                level="read"
                            >
                                <Settings
                                    meta={currentAdvisor}
                                    goBack={this._selectSection}
                                />
                            </PermissionsComponent>
                    }

                    {
                        this.state.selectedSection === 'advisor-reports' &&
                            <PermissionsComponent
                                permission="Reports"
                                accessType={this.props.accessType}
                                level="read"
                            >
                                <Reports
                                    meta={currentAdvisor}
                                    goBack={this._selectSection}
                                />
                            </PermissionsComponent>
                    }

                    {
                        this.state.selectedSection === 'advisor-files' &&
                            <Files
                                isMasterSite={ true }
                                type="advisors"
                                id={ this.props.currentAdvisor.advisorID }
                                parentFolder="files"
                                dispatch={ this.props.dispatch }
                                listFiles={ listFiles }
                                createFolder={ createFolder }
                                uploadFile={ uploadFile }
                                downloadFile={ downloadFile }
                                removeFile={ removeFile }
                                makePrivate={ makePrivate }
                                makePublic={ makePublic }
                                loader={ this.state.loader }
                            />
                    }
                </Content>

                {this.props.loader?.isLoading && <Loader isContainer/>}

                <span id="advisors-single" />
            </ContainerControlSite>
        );
    }
}

AdvisorDashboard.propTypes = {
    currentAdvisor: PropTypes.object,
    accessType: PropTypes.string
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    currentAdvisor: state.advisors.currentAdvisor,
    accessType: state.master.access
});

export default connect(mapStateToProps)(AdvisorDashboard);